import Checkbox from '@ben/orion/checkbox';
import Input from '@ben/orion/input';
import Select from '@ben/orion/select';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type {
  IModularBenefitDetail,
  IModularBenefitMessages,
} from '@ben/shared/types/columbia';
import { PolicyNumberType } from '@ben/shared/types/columbia';
import { memo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import ModelDropdown from '../model-select/model-dropdown';
import BenefitProductSelect from './benefit-product-select';

interface IManagerAndProviderProps {
  errors: Record<string, boolean>;
  warnings: IModularBenefitMessages;
  successes: IModularBenefitMessages;
  isScheduledVersion?: boolean;
}

const ManagerAndProvider = ({
  errors,
  warnings,
  successes,
  isScheduledVersion,
}: IManagerAndProviderProps) => {
  const { control } = useFormContext<IModularBenefitDetail>();

  return (
    <Wrapper>
      <HeaderContainer>
        <Typography variant={TypographyVariant.h2}>
          Manager and provider
        </Typography>
        <Typography variant={TypographyVariant.bodyMd1}>
          All fields below are required, unless specified optional.
        </Typography>
      </HeaderContainer>
      <Container>
        <Row>
          <Controller
            name="managedBy"
            control={control}
            rules={{
              required: fieldCannotBeBlankErrorMessage,
            }}
            render={({ field, fieldState }) => {
              const { value, onChange, ref } = field;
              const { isDirty, error } = fieldState;
              return (
                <ModelDropdown
                  key={value}
                  ref={ref}
                  name="Benefit manager"
                  apiName="benefit-managers"
                  selected={value}
                  onChange={onChange}
                  hasError={!!error || !!errors?.['managedBy']}
                  errorMessage={fieldCannotBeBlankErrorMessage}
                  warningMessage={isDirty ? undefined : warnings.managedBy}
                  successMessage={isDirty ? undefined : successes.managedBy}
                  width="100%"
                />
              );
            }}
          />
          <Controller
            name="broker"
            control={control}
            render={({ field }) => {
              const { value, onChange, ref } = field;
              return (
                <ModelDropdown
                  key={value}
                  ref={ref}
                  name="Broker (optional)"
                  apiName="benefits/brokers"
                  selected={Number(value)}
                  onChange={onChange}
                  width="100%"
                />
              );
            }}
          />
        </Row>
        <Row>
          <Controller
            name="provider"
            control={control}
            render={({ field }) => {
              const { value, onChange, ref } = field;
              return (
                <ModelDropdown
                  key={value}
                  ref={ref}
                  onChange={onChange}
                  name="Provider (optional)"
                  apiName="providers"
                  selected={value}
                  hasError={errors?.['provider']}
                  width="100%"
                  disabled={isScheduledVersion}
                />
              );
            }}
          />
          <Controller
            name="groupMembershipNumber"
            control={control}
            render={({ field, fieldState: { isDirty } }) => {
              const { value, ...rest } = field;
              return (
                <Input
                  {...rest}
                  name="Group membership number (optional)"
                  required={false}
                  width="100%"
                  value={value}
                  successMessage={
                    isDirty ? undefined : successes.groupMembershipNumber
                  }
                />
              );
            }}
          />
        </Row>
        <Row>
          <BenefitProductSelect />
          <Controller
            name="policyNumberType"
            control={control}
            render={({ field }) => {
              const { value, onChange } = field;
              const options = Object.values(PolicyNumberType).map((type) => ({
                label: type,
                value: type,
              }));
              return (
                <Select
                  onChange={onChange}
                  data={options}
                  label="Policy number type (optional)"
                  value={value}
                  width="100%"
                />
              );
            }}
          />
        </Row>
        <Row>
          <Controller
            name="clientBenefitCustomCode"
            control={control}
            rules={{
              required: false,
            }}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <Input
                  {...rest}
                  name="Client benefit custom code (optional)"
                  width="385px"
                  value={value || ''}
                />
              );
            }}
          />
          <CheckboxWrapper>
            <Controller
              name="isAuditable"
              control={control}
              render={({ field }) => (
                <Checkbox
                  label="Auditable"
                  {...field}
                  value={field.value ?? false}
                />
              )}
            />
          </CheckboxWrapper>
        </Row>
      </Container>
    </Wrapper>
  );
};

export default memo(ManagerAndProvider);

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    border: 1px solid ${theme.palette.neutral[60]};
    padding: ${theme.spacing(3)};
    border-radius: 10px;
  `}
`;

const HeaderContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `};
`;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    background: ${theme.palette.primary.bg};
    padding: ${theme.spacing(3)};
    border-radius: 10px;
  `}
`;

const Row = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
    width: 100%;
  `}
`;

const CheckboxWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-end;
    padding-bottom: ${theme.spacing(1)};
  `}
`;
